import { Clock, Vibrate } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { ActivityTypeIcon } from './ActivityTypeIcon';
import { formatDateTime } from '@/utils/dates';
import {
  formatDuration,
  getVibrationModeLabel,
  getActivityDisplayTitle,
  type Activity,
} from '@/types/activity';

interface ActivityCardProps {
  activity: Activity;
  onClick?: (activity: Activity) => void;
}

export function ActivityCard({
  activity,
  onClick,
}: ActivityCardProps) {
  const handleClick = () => {
    onClick?.(activity);
  };

  return (
    <Card
      className={`transition-all${onClick ? ' cursor-pointer hover:shadow-card-hover' : ''}`}
      onClick={onClick ? handleClick : undefined}
    >
      <CardBody>
        <div className="flex items-start gap-3">
          <ActivityTypeIcon type={activity.type} />

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-medium text-foreground">
                  {getActivityDisplayTitle(activity)}
                </p>
                <p className="text-sm text-muted">
                  {formatDateTime(activity.timestamp)}
                </p>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-muted">
              {activity.duration !== undefined && (
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span>{formatDuration(activity.duration)}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <Vibrate className="h-4 w-4" />
                <span>{getVibrationModeLabel(activity.vibrationMode)}</span>
              </div>
            </div>

            {(activity.steps || activity.distance) && (
              <div className="mt-3 flex flex-wrap gap-4 border-t border-border pt-3 text-sm">
                {activity.steps && (
                  <div>
                    <span className="font-medium text-foreground">
                      {activity.steps.toLocaleString()}
                    </span>
                    <span className="text-muted"> steps</span>
                  </div>
                )}
                {activity.distance && (
                  <div>
                    <span className="font-medium text-foreground">
                      {(activity.distance / 1000).toFixed(2)}
                    </span>
                    <span className="text-muted"> km</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
